import { useState } from 'react';

import {
  AutoComplete,
  Col,
  Divider,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import _ from 'lodash';

import { Form, SectionWrapper, WrapperSelect } from 'common/components';
import {
  AddNewItem,
  UpdateValueButton,
} from '../../drug-facts-label/components/DrugFactsButton';

import { useGetEnumsByEnumName } from 'hooks';
import { useFactPanelQasRequiredFields } from '../../../hook/useFactPanelQasRequiredFields';

import './Nutrients.less';
import { QaSpecHelpContentWithoutLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';
import { TOOlTIP_FIELDS } from 'static/tooltipFields';

const NUTRIENTS_FIELD_NAME = [
  'nutrientName',
  'limit',
  'guaranteed',
  'guaranteedUom',
  'quantity',
  'quantityUom',
];

const Nutrients = (props) => {
  const form = Form.useFormInstance();

  const { typeView, productId } = props;

  const nutrientsList = Form.useWatch('nutrients', form);

  const [mode, setMode] = useState();

  const onClickAddNew = () => {
    form.resetFields(NUTRIENTS_FIELD_NAME);
    setMode('add');
    form.setFieldsValue({ guaranteedUom: '%' });
  };

  const onClickCancel = () => {
    setMode('view');
  };

  const onClickAdd = () => {
    form
      .validateFields(NUTRIENTS_FIELD_NAME)
      .then((value) => {
        form.setFieldsValue({
          nutrients: [...(nutrientsList || []), value],
        });
        setMode('view');
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const isQaSpec = typeView === 'qa-spec';

  return (
    <div style={{ padding: '8px 0px' }} className='pet-food'>
      <NutrientsList setMode={setMode} mode={mode} isQaSpec={isQaSpec} />
      {mode === 'add' && (
        <AddNutrientsForm productId={productId} typeView={typeView} />
      )}
      <AddNewItem
        mode={mode}
        onClickAdd={onClickAdd}
        onClickCancel={onClickCancel}
        onClickAddNew={onClickAddNew}
      />
    </div>
  );
};

export default Nutrients;

const NutrientsList = ({ setMode, mode }) => {
  const form = Form.useFormInstance();
  const nutrientsList = Form.useWatch('nutrients', form);

  const onClickUpdate = (index) => {
    const cloneNutrientsList = _.cloneDeep(nutrientsList);

    form
      .validateFields(NUTRIENTS_FIELD_NAME)
      .then((value) => {
        cloneNutrientsList[index] = value;

        form.setFieldsValue({
          nutrients: cloneNutrientsList,
        });
        setMode('view');
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const onClickDelete = (index) => {
    const cloneNutrientsList = _.cloneDeep(nutrientsList);
    cloneNutrientsList.splice(index, 1);

    form.setFieldsValue({
      nutrients: cloneNutrientsList,
    });
  };

  const onClickEdit = (index, item) => {
    setMode(`edit-${index}`);
    form.setFieldsValue({ ...item });
  };

  const onCancel = () => {
    setMode('view');
  };

  return (
    <>
      <Form.Item name='nutrients' hidden />
      {nutrientsList?.map((item, index) => {
        const {
          nutrientName,
          quantity,
          quantityUom,
          guaranteed,
          guaranteedUom,
          limit,
        } = item;

        const isEdit = mode === `edit-${index}`;

        const perCupValue = quantity ? quantity + ' ' + quantityUom : '';
        const guaranteedValue = guaranteed + ' ' + guaranteedUom;

        return (
          <div key={index}>
            {isEdit ? (
              <>
                <AddNutrientsForm />
                <UpdateValueButton
                  onCancel={onCancel}
                  onClickUpdate={() => onClickUpdate(index)}
                />
              </>
            ) : (
              <SectionWrapper
                title={
                  <Space>
                    <EditOutlined onClick={() => onClickEdit(index, item)} />
                    <DeleteOutlined onClick={() => onClickDelete(index)} />
                  </Space>
                }
                placement='right'
                style={{ width: '100%', maxWidth: '100%' }}
              >
                <Row wrap={false} gutter={4}>
                  <Col span={15}>
                    <Typography.Text>
                      {nutrientName} <span>{limit ? `(${limit})` : ''}</span>
                    </Typography.Text>
                  </Col>

                  <Col span={5} style={{ textAlign: 'center' }}>
                    <Typography.Text ellipsis={{ tooltip: guaranteedValue }}>
                      {guaranteedValue}
                    </Typography.Text>
                  </Col>

                  <Col span={4} style={{ textAlign: 'center' }}>
                    <Typography.Text
                      ellipsis={{
                        tooltip: perCupValue,
                      }}
                    >
                      {perCupValue}
                    </Typography.Text>
                  </Col>
                </Row>
              </SectionWrapper>
            )}
          </div>
        );
      })}
    </>
  );
};

const AddNutrientsForm = (props) => {
  const form = Form.useFormInstance();
  const nutrients = Form.useWatch('nutrients', form);

  const { productId, typeView } = props;

  const petNutrientEnum = useGetEnumsByEnumName('PetNutrientEnum');
  const guaranteedUnitEnum = useGetEnumsByEnumName('GuaranteedUnitEnum');
  const petNutrientLimitEnum = useGetEnumsByEnumName('PetNutrientLimitEnum');
  const petNutrientQuantityUnitEnum = useGetEnumsByEnumName(
    'PetNutrientQuantityUnitEnum'
  );

  const { getRequiredRule } = useFactPanelQasRequiredFields({
    productId,
    typeView,
    section: 'qaSpecification.factsPanel.petNutritionLabel',
  });

  const isQaSpecType = typeView === 'qa-spec';

  const petFoodSuggestions = petNutrientEnum?.map((item) => {
    return {
      label: item?.enumDisplayName,
      value: item?.enumDisplayName,
    };
  });

  const quantity = Form.useWatch('quantity', form);

  return (
    <div className='pet-food__nutrient-form'>
      {nutrients?.length > 0 && <Divider style={{ margin: '8px 0' }} />}
      <Row gutter={4}>
        <Col span={24}>
          <Form.Item
            name='nutrientName'
            rules={[{ required: true, message: 'Nutrient Name is required' }]}
          >
            <QaSpecHelpContentWithoutLabel
              fieldPath={TOOlTIP_FIELDS.petNutrientName.fieldPath}
              isEnabled={isQaSpecType}
            >
              <AutoComplete
                options={petFoodSuggestions}
                filterOption={(inputValue, option) => {
                  return (
                    option?.label
                      ?.toLowerCase()
                      ?.indexOf(inputValue?.toLowerCase()) > -1
                  );
                }}
                getPopupContainer={(triggerNode) => {
                  return triggerNode;
                }}
              >
                <Input placeholder='Nutrient name' />
              </AutoComplete>
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name='limit'
            rules={[
              getRequiredRule({
                fieldName: ['nutrients', 'limit'],
                displayName: 'Measurement Limit',
              }),
            ]}
          >
            <QaSpecHelpContentWithoutLabel
              fieldPath={TOOlTIP_FIELDS.petNutrientLimit.fieldPath}
              isEnabled={isQaSpecType}
            >
              <WrapperSelect
                placeholder='Select measurement limit'
                getPopupContainer={(triggerNode) => {
                  return triggerNode;
                }}
                allowClear={true}
              >
                {[
                  petNutrientLimitEnum?.map((labelType, index) => {
                    return (
                      <Select.Option
                        key={index}
                        value={labelType?.enumDisplayName}
                      >
                        {labelType?.enumDisplayName}
                      </Select.Option>
                    );
                  }),
                ]}
              </WrapperSelect>
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
        <Col span={18}>
          <Form.Item
            name='guaranteed'
            rules={[{ required: true, message: 'Guaranteed is required' }]}
          >
            <QaSpecHelpContentWithoutLabel
              fieldPath={TOOlTIP_FIELDS.petNutrientGuaranteed.fieldPath}
              isEnabled={isQaSpecType}
            >
              <InputNumber placeholder='Guaranteed' style={{ width: '100%' }} />
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name='guaranteedUom'
            rules={[{ required: true, message: 'Guaranteed Unit is required' }]}
          >
            <QaSpecHelpContentWithoutLabel
              fieldPath={TOOlTIP_FIELDS.petNutrientGuaranteedUom.fieldPath}
              isEnabled={isQaSpecType}
            >
              <WrapperSelect
                getPopupContainer={(triggerNode) => {
                  return triggerNode;
                }}
                allowClear={true}
                placeholder='Unit'
              >
                {[
                  guaranteedUnitEnum?.map((labelType, index) => {
                    return (
                      <Select.Option
                        key={index}
                        value={labelType?.enumDisplayName}
                      >
                        {labelType?.enumDisplayName}
                      </Select.Option>
                    );
                  }),
                ]}
              </WrapperSelect>
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
        <Col span={18}>
          <Form.Item
            name='quantity'
            rules={[
              getRequiredRule({
                fieldName: ['nutrients', 'quantity'],
                displayName: 'Quantity',
              }),
            ]}
          >
            <QaSpecHelpContentWithoutLabel
              fieldPath={TOOlTIP_FIELDS.petNutrientQuantity.fieldPath}
              isEnabled={isQaSpecType}
            >
              <InputNumber
                onChange={() => form?.validateFields(['quantityUom'])}
                style={{ width: '100%' }}
                placeholder='Quantity'
              />
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name='quantityUom'
            rules={[
              getRequiredRule({
                fieldName: ['nutrients', 'quantityUom'],
                displayName: 'Quantity',
              }),
            ]}
          >
            <QaSpecHelpContentWithoutLabel
              fieldPath={TOOlTIP_FIELDS.petNutrientQuantityUom.fieldPath}
              isEnabled={isQaSpecType}
            >
              <WrapperSelect
                placeholder='Unit'
                getPopupContainer={(triggerNode) => {
                  return triggerNode;
                }}
                allowClear={true}
              >
                {[
                  petNutrientQuantityUnitEnum?.map((labelType, index) => {
                    return (
                      <Select.Option
                        key={index}
                        value={labelType?.enumDisplayName}
                      >
                        {labelType?.enumDisplayName}
                      </Select.Option>
                    );
                  }),
                ]}
              </WrapperSelect>
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};
